import { EmbeddingType } from './embeddings'

export type QualifierQuestion = {
  id: string
  questionType: QualificationQuestionAnswerQuestionType
  parsedQuestionTitle: LanguageContentObj[]
  parsedQuestionDescription?: LanguageContentObj[]
  parsedAnswerOptions?: QualifierAnswerObj[][]
  validatedAnswerOptions?: QualifierAnswerObj[][]
  eligibilityRuleType?: QualifierEligibilityRuleType
  answerValidityDays?: number
}

export enum SupportedLanguage {
  en = 'en',
  es = 'es',
}

export type LanguageContentObj = {
  language: SupportedLanguage
  content: string
}

export type QualifierAnswerObj = LanguageContentObj & {
  isEligible?: boolean
}

export const SUPPORTED_LANGUAGES = [SupportedLanguage.en, SupportedLanguage.es]

export const DEFAULT_LANGUAGE_CONTENT = {
  [SupportedLanguage.en]: '',
  [SupportedLanguage.es]: '',
}

export type LanguageContentMap = {
  [SupportedLanguage.en]?: string
  [SupportedLanguage.es]?: string
}

export enum QualificationQuestionAnswerQuestionType {
  FREEFORM = 'FREEFORM',
  MULTIPLE_CHOICE = 'MULTIPLE_CHOICE',
  ACKNOWLEDGMENT = 'ACKNOWLEDGMENT',
  SINGLE_CHOICE = 'SINGLE_CHOICE',
  VIDEO = 'VIDEO',
  PDF = 'PDF',
  IMAGES = 'IMAGES',
  SIGNATURE = 'SIGNATURE',
}

export enum QualifierEligibilityRuleType {
  MANUAL_REVIEW = 'MANUAL_REVIEW',
  ACCEPT_ANY_ELIGIBLE_ANSWER = 'ACCEPT_ANY_ELIGIBLE_ANSWER',
  ALWAYS_ELIGIBLE = 'ALWAYS_ELIGIBLE',
  REQUIRE_ALL_ELIGIBLE_ANSWERS = 'REQUIRE_ALL_ELIGIBLE_ANSWERS',
  REQUIRE_EXACT_MATCH = 'REQUIRE_EXACT_MATCH',
}

/**
 * Grading rules an ops author can pick for a MULTIPLE_CHOICE orientation question,
 * in display order. MANUAL_REVIEW is intentionally omitted — there is no human
 * review step in orientation scoring.
 */
export const ORIENTATION_MC_ELIGIBILITY_RULE_OPTIONS: QualifierEligibilityRuleType[] =
  [
    QualifierEligibilityRuleType.REQUIRE_ALL_ELIGIBLE_ANSWERS,
    QualifierEligibilityRuleType.REQUIRE_EXACT_MATCH,
    QualifierEligibilityRuleType.ACCEPT_ANY_ELIGIBLE_ANSWER,
    QualifierEligibilityRuleType.ALWAYS_ELIGIBLE,
  ]

export const ORIENTATION_MC_ELIGIBILITY_RULE_TO_LABEL: Record<
  QualifierEligibilityRuleType,
  string
> = {
  [QualifierEligibilityRuleType.REQUIRE_ALL_ELIGIBLE_ANSWERS]:
    'Require all correct (extra picks allowed)',
  [QualifierEligibilityRuleType.REQUIRE_EXACT_MATCH]:
    'Exact match (all correct, no incorrect)',
  [QualifierEligibilityRuleType.ACCEPT_ANY_ELIGIBLE_ANSWER]:
    'Accept any correct (at least one)',
  [QualifierEligibilityRuleType.ALWAYS_ELIGIBLE]: 'Always pass',
  [QualifierEligibilityRuleType.MANUAL_REVIEW]: 'Manual review',
}

export const QUESTION_TYPES_NOT_REQUIRING_QUESTION_TITLE = [
  QualificationQuestionAnswerQuestionType.SIGNATURE,
]

export const QUESTION_TYPES_REQUIRING_ANSWER_OPTIONS = [
  QualificationQuestionAnswerQuestionType.MULTIPLE_CHOICE,
  QualificationQuestionAnswerQuestionType.SINGLE_CHOICE,
  QualificationQuestionAnswerQuestionType.ACKNOWLEDGMENT,
]

export const QUESTION_TYPES_REQUIRING_MORE_THAN_ONE_ANSWER_OPTION = [
  QualificationQuestionAnswerQuestionType.MULTIPLE_CHOICE,
  QualificationQuestionAnswerQuestionType.SINGLE_CHOICE,
]

export const QUALIFICATION_QUESTION_TYPE_TO_TEXT = {
  [QualificationQuestionAnswerQuestionType.FREEFORM]: 'Text Input',
  [QualificationQuestionAnswerQuestionType.SINGLE_CHOICE]: 'Single Choice',
  [QualificationQuestionAnswerQuestionType.MULTIPLE_CHOICE]: 'Multi-Choice',
  [QualificationQuestionAnswerQuestionType.ACKNOWLEDGMENT]: 'Acknowledge',
  [QualificationQuestionAnswerQuestionType.VIDEO]: 'Video',
  [QualificationQuestionAnswerQuestionType.PDF]: 'PDF',
  [QualificationQuestionAnswerQuestionType.IMAGES]: 'Images',
  [QualificationQuestionAnswerQuestionType.SIGNATURE]: 'Signature',
}

export type QualificationQuestionAnswerCreateParams = {
  questionTitle: LanguageContentObj[]
  questionDescription?: LanguageContentObj[]
  answerOptions?: QualifierAnswerObj[][]
  questionType: QualificationQuestionAnswerQuestionType
  eligibilityRuleType?: QualifierEligibilityRuleType
  signature?: QualifierQuestionSignature
  embeddingTypes?: EmbeddingType[]
}

export type OrientationQuestionAnswerCreateParams = {
  questionTitle: LanguageContentObj[]
  questionDescription?: LanguageContentObj[]
  answerOptions?: QualifierAnswerObj[][]
  questionType: QualificationQuestionAnswerQuestionType
  eligibilityRuleType?: QualifierEligibilityRuleType
  signature?: QualifierQuestionSignature
}

export enum DocumentProvider {
  DOCUSIGN = 'DOCUSIGN',
  INHOUSE = 'INHOUSE',
}

export enum DocumentTemplateFieldType {
  SIGNATURE = 'SIGNATURE',
  INITIALS = 'INITIALS',
  PRINTED_NAME = 'PRINTED_NAME',
  DATE = 'DATE',
}

export type SignatureFieldConfig = {
  type: DocumentTemplateFieldType
  page: number
  xPercent: number
  yPercent: number
  widthPercent: number
  heightPercent: number
}

export type QualifierQuestionSignature = {
  templateId?: string
  eventType: string
  provider?: DocumentProvider
  storageKey?: string
  signatureFields?: SignatureFieldConfig[]
}

export type QualificationQuestionAnswerUpdateParams = {
  questionId: string
  questionTitle?: LanguageContentObj[]
  questionDescription?: LanguageContentObj[] | null
  answerOptions?: QualifierAnswerObj[][]
  eligibilityRuleType?: QualifierEligibilityRuleType
  embeddingTypes?: EmbeddingType[]
}
